import { useEffect, useState } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { Button } from '@merels/ui'
import { ArrowLeft, ShieldAlert, Info, History, Settings } from 'lucide-react'
import { useWebContainer } from './components/providers/DepProvider/DepProvider'
import { Publisher, Game, Project, TelemetryEvent } from './components/adminApp/types'
import { AccessDenied } from './components/adminApp/AccessDenied'
import { SidebarNav } from './components/adminApp/SidebarNav'
import { ProjectsList } from './components/adminApp/ProjectsList'
import { ProjectLogsView } from './components/adminApp/ProjectLogsView'
import { ProjectInfoView } from './components/adminApp/ProjectInfoView'
import { ProjectManageView } from './components/adminApp/ProjectManageView'

// Router helper to parse URL paths
const parseRoute = () => {
  const path = window.location.pathname
  if (path === '/admin' || path === '/admin/') {
    window.history.replaceState(null, '', '/admin/projects')
    return { view: 'list' as const, projectId: null as string | null }
  }
  if (path === '/admin/projects' || path === '/admin/projects/') {
    return { view: 'list' as const, projectId: null as string | null }
  }
  const match = path.match(/^\/admin\/projects\/([^\/]+)/)
  if (match) {
    return { view: 'detail' as const, projectId: decodeURIComponent(match[1]) }
  }
  return { view: 'list' as const, projectId: null as string | null }
}

export function AdminApp() {
  useSignals()
  const { auth, roomConnection } = useWebContainer()
  const [isAdministrator, setIsAdministrator] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [projects, setProjects] = useState<Project[]>([])
  
  // Custom router state
  const [route, setRoute] = useState(() => parseRoute())
  const [sidebarOpen, setSidebarOpen] = useState(false)
  
  // Project detail states
  const [activeSubTab, setActiveSubTab] = useState<'logs' | 'info' | 'manage'>('logs')
  const [projectLogs, setProjectLogs] = useState<TelemetryEvent[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [destinationPublisherSlug, setDestinationPublisherSlug] = useState('')
  const [transferring, setTransferring] = useState(false)
  const [deletingGame, setDeletingGame] = useState(false)
  
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  
  const workerUrl = roomConnection._workerUrl || ''

  const request = async (path: string, init?: RequestInit) => {
    const token = await auth.getToken()
    const headers = new Headers(init?.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (init?.body) headers.set('Content-Type', 'application/json')
    
    // Add router helper header if running in local worktree subdomain
    if (window.location.hostname.endsWith('.localhost')) {
      const subdomain = window.location.hostname.split('.')[0]
      headers.set('x-local-worktree', subdomain)
    }

    const response = await fetch(`${workerUrl}${path}`, { ...init, headers })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) throw new Error(data.error || 'Request failed')
    return data
  }

  const loadProjects = async () => {
    setError(null)
    try {
      const res = await request('/api/creator/admin/projects')
      setProjects(res.projects || [])
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to load projects')
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    setError(null)
    setNotice(null)
    try {
      await request(`/api/creator/admin/projects/${projectId}`, { method: 'DELETE' })
      setNotice(`Successfully deleted telemetry records for project ${projectId}.`)
      void loadProjects()
    } catch (cause: any) {
      setError(cause.message || 'Failed to delete project records')
    }
  }

  const checkAuthAndLoad = async () => {
    if (!auth.isLoaded.value) return
    if (!auth.currentUser.value) {
      setIsAdministrator(false)
      setLoading(false)
      return
    }
    
    setLoading(true)
    setError(null)
    try {
      const [_, projectsRes, catalogRes] = await Promise.all([
        request('/api/creator/admin/me'),
        request('/api/creator/admin/projects'),
        request('/api/creator/admin/catalog')
      ])
      
      setIsAdministrator(true)
      setProjects(projectsRes.projects || [])
      setPublishers(catalogRes.publishers || [])
      setGames(catalogRes.games || [])
    } catch (cause) {
      setIsAdministrator(false)
      setError(cause instanceof Error ? cause.message : 'Could not authenticate Merels Admin')
    } finally {
      setLoading(false)
    }
  }

  // Monitor popstate events to support browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setRoute(parseRoute())
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    void checkAuthAndLoad()
  }, [auth.isLoaded.value, auth.currentUser.value])

  // Load logs only for catalog rows that have a telemetry project.
  const loadProjectLogs = async (projectId: string) => {
    setLogsLoading(true)
    setNotice(null)
    try {
      const data = await request(`/api/creator/admin/telemetry?projectId=${projectId}&limit=200`)
      setProjectLogs(data.events || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load project logs')
    } finally {
      setLogsLoading(false)
    }
  }

  useEffect(() => {
    const project = projects.find((item) => item.projectId === route.projectId)
    if (project?.telemetryProjectId) {
      void loadProjectLogs(project.telemetryProjectId)
    } else {
      setProjectLogs([])
    }
  }, [route.projectId, projects])

  const handleProjectClick = (projectId: string) => {
    window.history.pushState(null, '', `/admin/projects/${encodeURIComponent(projectId)}`)
    setRoute({ view: 'detail', projectId })
    setActiveSubTab('logs')
    setDestinationPublisherSlug('')
    setNotice(null)
  }

  const handleBackToProjects = () => {
    window.history.pushState(null, '', '/admin/projects')
    setRoute({ view: 'list', projectId: null })
    setProjectLogs([])
    void loadProjects()
  }

  const handleTransfer = async (gameId: string) => {
    if (!destinationPublisherSlug) return
    const destination = publishers.find((p) => p.slug === destinationPublisherSlug)
    if (!destination) return
    if (!window.confirm(`Transfer ${gameId} to ${destination.slug}?`)) return

    setTransferring(true)
    setError(null)
    setNotice(null)
    try {
      await request('/api/creator/admin/games/transfer', {
        method: 'POST',
        body: JSON.stringify({ gameId, destinationPublisherSlug }),
      })
      setNotice(`Successfully transferred game to ${destinationPublisherSlug}!`)
      const catalog = await request('/api/creator/admin/catalog')
      setPublishers(catalog.publishers || [])
      setGames(catalog.games || [])
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to transfer project')
    } finally {
      setTransferring(false)
    }
  }

  const handleDeleteGame = async (gameId: string) => {
    if (!window.confirm(`Remove ${gameId} from the catalog? This permanently deletes its versions and channels, but keeps its project credentials for republishing.`)) return

    setDeletingGame(true)
    setError(null)
    setNotice(null)
    try {
      await request(`/api/creator/admin/games/${encodeURIComponent(gameId)}`, { method: 'DELETE' })
      setGames((currentGames) => currentGames.filter((game) => game.gameId !== gameId))
      setNotice(`Removed ${gameId} from the catalog.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to remove game from catalog')
    } finally {
      setDeletingGame(false)
    }
  }

  if (!auth.isLoaded.value || loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0d0e12] text-xs text-muted-foreground">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="uppercase tracking-widest text-[#94a3b8]">Loading Merels Admin…</span>
        </div>
      </div>
    )
  }

  if (isAdministrator === false) {
    return <AccessDenied userId={auth.currentUser.value?.id} />
  }

  const activeProject = projects.find(p => p.projectId === route.projectId)
  const activeGameId = activeProject?.gameId || (activeProject?.publisherSlug && activeProject?.gameSlug
    ? `${activeProject.publisherSlug}/${activeProject.gameSlug}` 
    : null)
  const selectedGame = games.find(g => g.gameId === activeGameId) || (activeProject?.gameId && activeProject.publisherSlug && activeProject.gameSlug
    ? {
        gameId: activeProject.gameId,
        gameSlug: activeProject.gameSlug,
        publisherSlug: activeProject.publisherSlug,
        publisherDisplayName: activeProject.publisherSlug,
        displayName: activeProject.displayName || activeProject.gameSlug,
        versionCount: activeProject.versionCount || 0,
      }
    : null)
  const destinations = publishers.filter(p => p.slug !== activeProject?.publisherSlug)

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#0a0b0d] text-gray-200 font-sans text-xs">
      <SidebarNav
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        routeView={route.view}
        userId={auth.currentUser.value?.id}
        onBackToProjects={handleBackToProjects}
      />

      <main className="flex-1 flex flex-col h-full overflow-y-auto bg-[#0a0b0d] p-6">
        {error && (
          <div className="mb-4 rounded-md border border-red-900/40 bg-red-950/10 p-3 text-red-400 flex items-center space-x-2 shrink-0">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="mb-4 rounded-md border border-emerald-900/40 bg-emerald-950/10 p-3 text-emerald-400 flex items-center space-x-2 shrink-0">
            <Info className="h-4 w-4 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {route.view === 'list' ? (
          <ProjectsList
            projects={projects}
            onRefresh={loadProjects}
            onProjectClick={handleProjectClick}
            onDeleteProject={handleDeleteProject}
          />
        ) : (
          <div className="space-y-5 flex flex-col flex-1">
            <div className="flex items-center justify-between border-b border-[#1e212f] pb-4 shrink-0">
              <div className="flex items-center space-x-4">
                <Button 
                  onClick={handleBackToProjects} 
                  variant="secondary" 
                  className="h-8 px-3 flex items-center space-x-1.5 bg-[#141620] hover:bg-[#1f2231] text-gray-300 border border-[#222535]"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span>Projects</span>
                </Button>
                <div>
                  <div className="flex items-center space-x-2.5">
                    <h2 className="text-sm font-bold text-gray-100">
                      {activeProject?.publisherSlug && activeProject?.gameSlug
                        ? `${activeProject.publisherSlug}/${activeProject.gameSlug}`
                        : activeProject?.gameSlug
                          ? `.../${activeProject.gameSlug}`
                          : '...'
                      }
                    </h2>
                    <span className="font-mono text-[9px] bg-[#1a1c29] text-gray-400 border border-[#272b3f] px-2 py-0.5 rounded uppercase">
                      {route.projectId}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Workspace path tracking. Owner: <span className="text-gray-300 font-semibold">{activeProject?.ownerName || 'Unknown'}</span>
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-1 bg-[#12141d] p-1 rounded-md border border-[#1e212f]">
                <button
                  onClick={() => setActiveSubTab('logs')}
                  className={`flex items-center space-x-1.5 px-3 py-1 rounded text-[10px] font-bold uppercase transition-all ${
                    activeSubTab === 'logs' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-gray-200'
                  }`}
                >
                  <History className="h-3 w-3" />
                  <span>Logs</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('info')}
                  className={`flex items-center space-x-1.5 px-3 py-1 rounded text-[10px] font-bold uppercase transition-all ${
                    activeSubTab === 'info' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-gray-200'
                  }`}
                >
                  <Info className="h-3 w-3" />
                  <span>Info</span>
                </button>
                <button
                  onClick={() => setActiveSubTab('manage')}
                  className={`flex items-center space-x-1.5 px-3 py-1 rounded text-[10px] font-bold uppercase transition-all ${
                    activeSubTab === 'manage' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-gray-200'
                  }`}
                >
                  <Settings className="h-3 w-3" />
                  <span>Manage</span>
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-[300px]">
              {activeSubTab === 'logs' && (
                <ProjectLogsView 
                  logs={projectLogs} 
                  loading={logsLoading} 
                  onRefresh={() => activeProject?.telemetryProjectId && loadProjectLogs(activeProject.telemetryProjectId)}
                />
              )}

              {activeSubTab === 'info' && (
                <ProjectInfoView
                  projectId={route.projectId!}
                  project={activeProject}
                  selectedGame={selectedGame}
                />
              )}

              {activeSubTab === 'manage' && (
                <ProjectManageView
                  activeGameId={activeGameId}
                  selectedGame={selectedGame}
                  destinationPublisherSlug={destinationPublisherSlug}
                  setDestinationPublisherSlug={setDestinationPublisherSlug}
                  destinations={destinations}
                  transferring={transferring}
                  onTransfer={handleTransfer}
                  deleting={deletingGame}
                  onDelete={handleDeleteGame}
                />
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
